import { ANALYTICS_INPUT_FILE_EXTENSIONS } from "./fileTypes";

import type { AnalyticsBoundBy } from "../models/audit/analyticsEnums";
import type { InputBinding } from "../models/audit/analyticsColumn";
import type { AnalyticsFileParameter } from "../models/audit/analyticsWorkflow";

/**
 * The rules the two file-binding surfaces share, as pure functions over the
 * projected parameters, the stored bindings and the session's documents.
 *
 * A *feed* is the auditor-facing name for a declared file parameter — the same
 * object the projection endpoint calls a parameter. Using the two words as if
 * they named different things is how a second, redundant surface gets built, so
 * everything here is keyed on `paramKey` and nothing invents a second identity
 * for it.
 *
 * Nothing here reaches the network and nothing here holds state: both surfaces
 * derive their whole display from the endpoints' own responses, which is what
 * makes an assignment survive a reload rather than living in a component.
 */

/** One analytics column's declared parameters beside its stored assignments. */
export interface FeedColumn {
  columnId: string;
  columnName: string;
  /**
   * The name of the workflow bound to this column, which is what the binding
   * editor heads the column's parameters with.
   *
   * Not interchangeable with `columnName`: a column is named for the assertion
   * it makes about a sample, the workflow for the analytic that reaches it, and
   * the two are authored separately. Null until a workflow is attached — the
   * column exists first, which is the `not_configured` state.
   */
  workflowName: string | null;
  parameters: readonly AnalyticsFileParameter[];
  bindings: Readonly<Record<string, InputBinding>>;
}

/**
 * The id a binding names a document by.
 *
 * A session's document row carries two ids of different spaces as plain
 * strings — the document's own under `documentId`, the row's own under `id` —
 * and rows hand them to the same fields from separate sources. Only a brand
 * keeps them apart: a row id put where a bound document id belongs survives the
 * round trip, then matches no row, so `fillsSubject` misses it and the parameter
 * reads as filled by another file while the bind route answers 404.
 *
 * `documentKey` is the only way to mint one from a row, so the choice between
 * the two halves is made once for every surface rather than separately at each
 * of them; the one other way in is the stored binding, narrowed where it is
 * read. It admits any non-empty id and answers null for the row that fills
 * neither half, which is a document no binding can name: a caller reading that
 * null has nothing to bind, and says so by leaving the binding surfaces off the
 * row.
 */
export type BoundDocumentId = string & {
  readonly __boundDocumentId: unique symbol;
};

/** One assignable pair — the coordinates a per-document boolean cannot carry. */
export interface BindingTarget {
  columnId: string;
  columnName: string;
  /** The bound workflow's name, carried so the editor can head its groups. */
  workflowName: string | null;
  paramKey: string;
  label: string;
  /**
   * What the workflow's builder wrote about the parameter in Optro Analytics, or
   * null when they wrote nothing. AuditBoard sends an empty string for an input
   * with no description, so blank text is read as none rather than drawn as an
   * empty line under the name.
   */
  description: string | null;
  required: boolean;
  /**
   * Which class of binding the pair holds, or null while it holds none.
   *
   * Read from the stored binding because the parameter carries no discriminator
   * of its own: the projection declares a key, a label, a description and
   * whether the parameter is required, and nothing about where its file comes
   * from.
   */
  boundSource: InputBinding["source"] | null;
  /**
   * The document a `document` binding names; null in every other state.
   *
   * Null too for a stored binding whose id is not one a document can be keyed
   * on, which reads it as filled by a file this table cannot name — the same
   * reading a named file the name map misses already takes, and the true one:
   * the parameter is answered, and no row here is the answer.
   */
  assignedDocumentId: BoundDocumentId | null;
  boundBy: AnalyticsBoundBy | null;
}

/**
 * The write bodies for the assignments a row can offer.
 *
 * Two of the three classes of assignment. The third is the test table's own
 * marked export, whose file is whichever one the table marks rather than one any
 * row is the subject of, so no control here can assert it and nothing here
 * builds its body — an export binding stored on a parameter is written
 * elsewhere, and this file only reads it.
 *
 * None carries a `boundBy` or a `confidence`: both are properties of the writer,
 * not of the request, and the route stamps them. A body that could claim
 * `'agent'` for a human's own assignment would put a false attribution in the
 * record of how a test was configured.
 */

/** One document answering the parameter for the whole population. */
export interface DocumentAssignmentRequest {
  columnId: string;
  paramKey: string;
  source: "document";
  documentId: BoundDocumentId;
}

/**
 * The declaration that the sample's own row, as a file, answers the parameter.
 *
 * It names no document and no sample. The row is built from the population when
 * the scope is resolved, so the sample is the one being resolved for and an
 * identifier here would name a sample the run may not be testing; the route reads
 * a `documentId` beside this source as a contradiction, as it does for a sample
 * export.
 *
 * It is the only class whose content varies per sample. The other two hand the
 * same file to every sample of the column, so a workflow bound only to those
 * analyses one body of data for the whole population while recording a
 * conclusion per sample — it is never told which sample it is testing.
 */
export interface SampleRowAssignmentRequest {
  columnId: string;
  paramKey: string;
  source: "sample_row";
}

/**
 * The copy for the analytics-input flag and its indicator.
 *
 * Each string names **both** roles the file plays, so a reader learns why the
 * file is marked rather than only that it is.
 */
export const ANALYTICS_INPUT_COPY = {
  flagLabel: "Evidence and analytics input",
  flagHelper:
    "Keep this file as the sample's evidence and also use it as an input to the analytics workflow. One answer per file, applied everywhere the file is used.",
  indicatorTitle: "Sample evidence and analytics input",
  flagColumnHeading: "For Analytics Test",
  // The switch's own state in words. A switch states what it is, not what
  // pressing it would do, so these read as the current answer and never as an
  // instruction.
  flagOn: "Yes",
  flagOff: "No",
  // The third answer, for a row drawn before — or without — the document record
  // it describes. "No" there is a claim nobody made, and beside a disabled
  // switch it reads as a file the workspace has ruled out. Two strings because
  // the column is 160px wide: the short one is the state, the long one is its
  // `title`.
  flagUnknown: "Not loaded",
  flagUnknownTitle:
    "This file's details have not loaded, so whether it is an analytics input is not known yet. Reload the page to try again.",
  // Rendered beside the control rather than in place of it: a control that
  // vanished for some files would read as a missing feature. The reason names
  // the requirement rather than the file, because the auditor's next move is to
  // find a different file, not to argue about this one. The formats are named
  // off the list rather than described, because "spreadsheets and CSVs" is not
  // the rule: a run also needs a reader for the format, and it has none for
  // binary `.xlsb`.
  notFlaggable: `An analytics input has to be a format a workflow can read rows out of: ${ANALYTICS_INPUT_FILE_EXTENSIONS.join(", ")}.`,
} as const;

/**
 * Zip the procedure's analytics columns with the feeds read for them.
 *
 * The caller's column order is the authoring order the auditor sees elsewhere,
 * so it wins over the order the reads came back in. A column whose feeds have
 * not arrived contributes nothing rather than an empty parameter list, which
 * would render as "this workflow declares no files" while the read is still in
 * flight.
 *
 * A column whose workflow is deleted contributes nothing either, whatever its
 * bindings hold: there is no workflow left to feed, so a pair offered for it
 * would be an assignment no run can consume.
 */
export function feedColumns(input: {
  columns: readonly {
    columnId: string;
    columnName: string;
    workflowName: string | null;
  }[];
  feeds: readonly {
    columnId: string;
    parameters: readonly AnalyticsFileParameter[];
    bindings: Readonly<Record<string, InputBinding>>;
    workflowDeleted: boolean;
  }[];
}): FeedColumn[] {
  return input.columns.flatMap((column) => {
    const feed = input.feeds.find(
      (entry) => entry.columnId === column.columnId,
    );
    if (feed === undefined || feed.workflowDeleted) {
      return [];
    }
    return [
      {
        columnId: column.columnId,
        columnName: column.columnName,
        workflowName: column.workflowName,
        parameters: feed.parameters,
        bindings: feed.bindings,
      },
    ];
  });
}

/**
 * The names of the columns `feedColumns` dropped for a deleted workflow, in the
 * same authoring order.
 *
 * Needed beside the targets because the targets alone cannot say it: a deleted
 * column contributes nothing, which is also what a workflow declaring no file
 * inputs contributes, and only one of those may be reported as such.
 */
export function deletedWorkflowColumnNames(input: {
  columns: readonly { columnId: string; columnName: string }[];
  feeds: readonly { columnId: string; workflowDeleted: boolean }[];
}): string[] {
  return input.columns.flatMap((column) =>
    input.feeds.some(
      (feed) => feed.columnId === column.columnId && feed.workflowDeleted,
    )
      ? [column.columnName]
      : [],
  );
}

/**
 * Which of the five answers the feeds read has produced.
 *
 * An in-flight read, a failed read and a workflow that genuinely declares no
 * file parameters all reach a rendering surface as an empty parameter list, and
 * only the last of them licenses saying so. Carried as a state because no
 * property of the list itself can tell them apart.
 *
 * `stale` is a list that arrived and whose last refresh failed. It is still
 * worth rendering — it is the auditor's own configuration, and the alternative
 * is an empty editor — but nothing may be *asserted* over it, since the client
 * already knows it is behind the server.
 *
 * `unasked` is the read nobody issued, which a disabled query reports as the
 * same empty, unerrored, not-running result an in-flight read has. It is named
 * apart from `loading` because it is the one state that no waiting resolves:
 * there is nothing running to produce the answer a surface would be waiting
 * for, so a surface that treats it as `loading` waits forever.
 */
export type FeedsReadState =
  | "unasked"
  | "loading"
  | "failed"
  | "stale"
  | "ready";

/**
 * Read one feeds query's state.
 *
 * An answer wins over a failure: a failed background refetch keeps the list it
 * already had, and replacing a usable list with an error would take the
 * auditor's work away over a failure the toast has already reported. It reads as
 * `stale` rather than `ready` so that the surfaces which merely display the list
 * keep displaying it while the ones that make a claim about the server can
 * decline to — `ready` is the one state in which the held list is the server's
 * own answer. A read in flight wins over a failure for the same reason in the
 * other direction — a retry that is still running has not failed.
 *
 * `issued` separates the last two, and it has to be told rather than read off
 * the query: a query its caller holds disabled reports exactly what a query
 * whose first fetch is still open reports — no data, not loading, no error — so
 * nothing in the read itself tells a read that is about to answer from one that
 * never will.
 */
export function feedsReadState(read: {
  /** The read returned, however many columns it named. */
  answered: boolean;
  /** The query was enabled, so some fetch of it has been started. */
  issued: boolean;
  isLoading: boolean;
  error: Error | null;
}): FeedsReadState {
  if (read.answered) return read.error !== null ? "stale" : "ready";
  if (read.isLoading) return "loading";
  if (read.error !== null) return "failed";
  return read.issued ? "loading" : "unasked";
}

/**
 * The vocabulary both binding surfaces speak.
 *
 * Shared rather than written twice, because the Documents tab and the Support
 * tab describe the same union: the moment the two disagree on what a
 * sample-export parameter is called, an auditor reading both concludes they are
 * two different things.
 *
 * There is no expected-filename string here, and none is to be added. No
 * filename reaches Midship at all — the workflow engine's `FILE` parameter
 * carries no value, unlike every other parameter type — so the auditor is told
 * which slot they are filling and what belongs in it, never which file to go and
 * find. A slot identified by what it is *for* also survives the auditor
 * exporting the same data under a different name; a filename does not.
 */
export const INPUT_BINDING_COPY = {
  // The one question every row's control answers, whichever subject the row
  // carries. A file's row and the sample row's row offer the same menu of
  // workflow inputs, and a heading that changed with the row would make one
  // vocabulary read as two.
  pickerHeading: "This row fills…",
  // The run-time row as the subject of a row of its own.
  //
  // The caption is the sentence that describes it, and it renders here rather
  // than under every parameter in the picker: the run-time answer has one row
  // now, so the explanation has one place to be, beside the thing it explains.
  //
  // Spelled for a sample basis alone, because that is the only basis the feed
  // is offered on. A direct-basis table holds one placeholder row seeded when
  // the session is created, and no surface writes its attribute values, so the
  // feed there resolves to a header line over the word "placeholder".
  sampleRowTitle: "Each sample's own row",
  sampleRowCaption:
    "built from the population at run time — one row per sample",
  // What stands in the parameters column while the row's switch is off. Stated
  // rather than left blank, because a blank cell reads as *this column does not
  // apply to this row*, and the switch beside it is what makes it apply. It is a
  // claim the row can make only when it holds no answer: a sample-row binding
  // keeps the switch on, so off always means this row fills nothing.
  sampleRowNotUsed: "not used for this test",
  // What stands where a document category would. The row has no document until
  // the scope is resolved, so it has nothing to categorise.
  sampleRowDocument: "generated at run time",
  // What fills a parameter, read from a row that is not what fills it. Named
  // rather than left to an unticked box: an unticked box is also what an
  // unanswered parameter looks like, and the two ask different things of the
  // auditor — taking an answer off a sibling is a decision, and answering a
  // parameter nothing holds is not.
  filledBy: "filled by",
  // The filler is a document this table cannot name — one dropped from the
  // session, or one a read has not returned. Still reported: silence here reads
  // as unanswered, which is the one wrong thing this line can say.
  filledByAnotherFile: "filled by another file",
  filledBySampleExport: "filled by this test table's own export",
  filledBySampleRow: "filled by each sample's own row",
  // The clear offered beside a filler no row of this table is the subject of.
  // It names the answer rather than the filler, because the one control stands
  // beside two different fillers and the auditor's move is the same for both.
  clearFiller: "Clear this answer",
  // A required parameter nothing fills, phrased as the state it is in rather
  // than as an instruction: which row should answer it is the auditor's
  // decision, and no single row is the right one to be told to.
  unfilled: "nothing fills this yet",
  required: "Required",
  // One name for the parameters column, shared by its header and by the
  // accessible name of every row's trigger: a header names the column once for
  // a sighted reader, and one heading cannot say which row a trigger belongs to.
  parametersColumnHeading: "Workflow Parameters",
  // The closed cell names its selection, so with nothing selected it has to
  // read as an empty select. An imperative here reads as a button, and hides
  // that the cell has a value to be read at all.
  cellNoAssignments: "Select a parameter",
  // The closed cell on a row the analytics-input flag has settled as No, where
  // the whole control is inert. Stated as the row's condition rather than left
  // as an empty cell: an empty cell reads as *this column does not apply to
  // this row*, and what is true is that this row could fill a parameter once it
  // is flagged. Phrased as a fact and not as an instruction, because the fix is
  // in the flag column and no press here can reach it.
  cellFillsNothing: "Fills nothing",
  emptyFeeds: "This workflow declares no file inputs.",
  // Said in place of the empty-feeds line, never beside it, for a column whose
  // workflow is gone: a deleted workflow declares nothing because there is
  // nothing left to declare, and "declares no file inputs" would send the
  // auditor to look for a configuration fault. The column is named by the name
  // the table shows it under, not by the workflow's authored name, which is
  // free text an upstream rename leaves behind.
  workflowDeleted: (columnNames: readonly string[]) =>
    columnNames.length === 1
      ? `The workflow behind ${columnNames[0]} was deleted in Optro Analytics, so it has no file inputs left to fill.`
      : `The workflows behind ${columnNames.slice(0, -1).join(", ")} and ${columnNames[columnNames.length - 1]} were deleted in Optro Analytics, so they have no file inputs left to fill.`,
  // One failure for the whole read, because that is what it is: every column is
  // fetched inside one `Promise.all`, so a single column's refusal empties every
  // column's feeds. Copy naming this column would send the auditor looking for a
  // fault in a column that may well be fine.
  feedsUnavailable:
    "Could not load the workflow's file inputs, so none can be assigned. The read covers every analytics test on this table at once, so this is not one test's failure. Reload the page to try again.",
} as const;

export function bindingTargets(
  columns: readonly FeedColumn[],
): BindingTarget[] {
  return columns.flatMap((column) =>
    column.parameters.map((parameter) => {
      const binding = column.bindings[parameter.paramKey];
      return {
        columnId: column.columnId,
        columnName: column.columnName,
        workflowName: column.workflowName,
        paramKey: parameter.paramKey,
        label: parameter.label ?? parameter.paramKey,
        description: parameter.description?.trim() || null,
        required: parameter.required,
        boundSource: binding?.source ?? null,
        assignedDocumentId:
          binding?.source === "document" &&
          isBoundDocumentId(binding.documentId)
            ? binding.documentId
            : null,
        boundBy: binding?.boundBy ?? null,
      };
    }),
  );
}

/**
 * Whose row a binding control is being operated from — the one thing a tick on
 * that row is allowed to mean.
 *
 * No per-table-row control may map a file other than the file its row is for, so
 * the subject is the whole of what a row can assert: a document row can only say
 * *this document fills that input*, and the sample row can only say *the row
 * built per sample fills it*. The test table's own export is not a subject,
 * because the file it resolves to is whichever one the table marks, which on a
 * table marking two files is some other row's.
 */
export type BindingSubject =
  | { kind: "document"; documentId: BoundDocumentId }
  | { kind: "sample_row" };

/** Whether this row's own subject is what fills the parameter. */
export function fillsSubject(
  target: BindingTarget,
  subject: BindingSubject,
): boolean {
  if (subject.kind === "sample_row") return target.boundSource === "sample_row";
  return (
    target.boundSource === "document" &&
    target.assignedDocumentId === subject.documentId
  );
}

/** The pairs this row's subject fills, out of targets the caller already has. */
export function targetsFilledBy(
  targets: readonly BindingTarget[],
  subject: BindingSubject,
): BindingTarget[] {
  return targets.filter((target) => fillsSubject(target, subject));
}

/**
 * Whether any parameter is answered by the test table's own marked export.
 *
 * The run's own gate on every claim about the marked file: it resolves the flag
 * only for a column that asks for it, so a table with nothing bound to the
 * export generates fine whatever it marks and however far that file reaches.
 *
 * One predicate for the sentences under the table and for the marker on the row,
 * so a row cannot carry an alarm the prose beside it declines to raise.
 */
export function boundToSampleExport(
  targets: readonly BindingTarget[],
): boolean {
  return targets.some((target) => target.boundSource === "sample_export");
}

/**
 * What a parameter's answer is called on a row that is not the answer.
 *
 * A table rather than a chain of comparisons for the answers that name no
 * document, so a fourth source added to the binding union fails to compile here
 * instead of quietly reading as unanswered.
 */
const RESOLVED_FILLER_NAMES: Record<
  Exclude<InputBinding["source"], "document">,
  string
> = {
  sample_export: INPUT_BINDING_COPY.filledBySampleExport,
  sample_row: INPUT_BINDING_COPY.filledBySampleRow,
};

/**
 * What fills this parameter today, or null when nothing does.
 *
 * Takes the target rather than the table, so the sentence belongs to the one
 * parameter it is rendered against: a note derived from the table as a whole
 * prints the same string beside every parameter and attributes one parameter's
 * answer to another's.
 *
 * The names come from the caller because a binding stores a document id and no
 * name at all. A file the map cannot name is still reported as filled — the id
 * would say nothing to an auditor, and silence would say the parameter is free.
 */
export function fillerName(
  target: BindingTarget,
  documentNames: ReadonlyMap<BoundDocumentId, string>,
): string | null {
  const source = target.boundSource;
  if (source === null) return null;
  if (source !== "document") return RESOLVED_FILLER_NAMES[source];
  const name =
    target.assignedDocumentId === null
      ? undefined
      : documentNames.get(target.assignedDocumentId);
  return name === undefined
    ? INPUT_BINDING_COPY.filledByAnotherFile
    : `${INPUT_BINDING_COPY.filledBy} ${name}`;
}

/**
 * Whether a filled parameter has no row of this table as its subject.
 *
 * Every other answer is emptied by a second press on the row that owns it,
 * which is what lets a tick mean exactly one thing — *this row fills it* —
 * everywhere it appears. Two answers have no such row: a `sample_export`
 * binding names whichever file the table marks, which `BindingSubject` cannot
 * express by design, and a `document` binding naming a file this table cannot
 * name has lost the row that would carry it. Both can be replaced from any row,
 * and without a control of their own neither can be emptied from any.
 *
 * No row writes an export binding either: one stored on a parameter is the
 * backend's or the agent's. It is still read and named wherever a parameter
 * holds it, so it stays legible and can be replaced.
 *
 * Decided on the same map `fillerName` reads, so the parameters singled out here
 * are exactly the ones whose note declines to name a file of this table's.
 *
 * A switch rather than a chain of comparisons, so a fourth source added to the
 * binding union fails to compile here instead of quietly reading as ownable.
 */
export function filledByNoRow(
  target: BindingTarget,
  documentNames: ReadonlyMap<BoundDocumentId, string>,
): boolean {
  switch (target.boundSource) {
    case null:
    case "sample_row":
      return false;
    case "sample_export":
      return true;
    case "document":
      return (
        target.assignedDocumentId === null ||
        !documentNames.has(target.assignedDocumentId)
      );
  }
}

/**
 * Whether a string can stand as the id a binding names a document by.
 *
 * A predicate rather than an assertion, because `string & {...}` is assignable
 * to `string`: narrowing is all the brand ever needed, so nothing here has to
 * claim of a value what the compiler cannot see for itself.
 *
 * Emptiness is the whole of the test, and the whole of what this can decide: the
 * two ids a row carries are opaque strings of the same shape, and which half is
 * which is known only where the row itself is in hand. What it does settle is
 * the row that fills neither — an empty id names no document, so a parameter
 * bound to it would read as filled by a file nothing can find.
 *
 * Private, so that `documentKey` and the stored binding read above stay the only
 * ways in and no caller elsewhere can name a document to a binding without
 * going through them.
 */
function isBoundDocumentId(id: string): id is BoundDocumentId {
  return id.length > 0;
}

/**
 * The id a binding names a document by, from the row that carries both halves.
 *
 * A session's document row carries the document's own id under `documentId` and
 * its own under `id`, and not every projection fills the first. One function so
 * that a map built here and a lookup made there cannot pick different halves of
 * that fallback and miss each other.
 *
 * Exported as the only constructor client code has: every surface that names a
 * document to a binding hands the row over rather than choosing a half itself,
 * which is what stops one document being keyed three ways.
 *
 * Null for the row that fills neither half. Such a row names no document, so a
 * caller holding the null has nothing to bind rather than something to bind
 * carefully, and the binding surfaces belong off it.
 */
export function documentKey(document: {
  id: string;
  documentId: string;
}): BoundDocumentId | null {
  const id = document.documentId || document.id;
  return isBoundDocumentId(id) ? id : null;
}

/**
 * Index the session's documents by the id a binding names them with.
 *
 * One function for both tabs: they hold the same document shape under different
 * names, and two copies of this would be two chances to key the map on `id`
 * where a binding carries `documentId`.
 */
export function documentNamesById(
  documents: readonly {
    id: string;
    documentId: string;
    name: string | null;
    fetchableDocument: { fileName: string } | null;
  }[],
): Map<BoundDocumentId, string> {
  const names = new Map<BoundDocumentId, string>();
  for (const document of documents) {
    const fileName = document.fetchableDocument?.fileName ?? document.name;
    if (fileName === null) continue;
    const key = documentKey(document);
    // A row no binding can name is a row no lookup comes here for, so it is left
    // out on the same terms as the file with no name to report.
    if (key === null) continue;
    names.set(key, fileName);
  }
  return names;
}

/** One document on the test table, in the fields the sample-export rules read. */
export interface TestTableDocument {
  id: string;
  documentId: string;
  /**
   * The analytics-input flag as the projection sent it.
   *
   * Absent is a third answer — the field was not sent — and only `true` is a
   * flagged document. An unsent flag counted as a flag would report a collision
   * the run would not refuse over, off a fact nobody stated.
   */
  isAnalyticsInput?: boolean;
}

/** One sample of the test table, in the documents it holds as evidence. */
export interface TestTableSourceRow {
  matchItems: readonly { sourceDocument: { id: string } }[];
}

/**
 * How many of this test table's samples hold the document as evidence.
 *
 * The flag is resolved at the table — one document is the whole table's
 * analytics input — but the document reaches a sample only through that
 * sample's own match, so reach is a count and not a fact about the table. A
 * table-wide "matched somewhere" answers the wrong question: a file matched to
 * two samples of five is a file three samples have no input for, and collapsing
 * that to a yes hides the shortfall from the only person who can close it.
 *
 * Compared on `documentKey`'s id, which is the id a binding names a document
 * by. A count keyed on the other half of that fallback would silently read as
 * zero for every document whose two ids differ.
 *
 * Each sample is counted once however many of its match items name the
 * document, because the numbers are read against the sample count beside them.
 */
function samplesReaching(
  sourceRows: readonly TestTableSourceRow[],
  documentId: BoundDocumentId,
): number {
  let reaching = 0;
  for (const sourceRow of sourceRows) {
    const reaches = sourceRow.matchItems.some(
      (matchItem) => matchItem.sourceDocument.id === documentId,
    );
    if (reaches) reaching += 1;
  }
  return reaching;
}

/**
 * The copy for the sample column's count.
 *
 * A heading of its own only where the table has no sample column already: the
 * Documents tab draws the count under `Sample Match`, which the same numbers
 * answer, and a second heading there would split one question into two columns.
 */
export const SAMPLE_REACH_COPY = {
  columnHeading: "Samples Matched",
} as const;

/**
 * What a sentence about an analytics input is claiming.
 *
 * `refusal` is a run that does not start: press Generate and the column reports
 * this instead of a result. `shortfall` is a run that starts and answers some of
 * the samples, leaving the rest with no input. They are different jobs for the
 * auditor and different news about the table, and rendered alike the second
 * reads as the first.
 *
 * The kind, not the styling: which icon and which colour say it is the reader's
 * surface to decide, and the same kind is drawn twice — once under the table and
 * once on the row.
 */
export type AnalyticsInputWarningKind = "refusal" | "shortfall";

/**
 * What the glyph stands for, in words, for a surface whose visible text does not
 * already say it.
 *
 * Keyed on the kind the glyph itself is drawn from, and kept in the file that
 * owns that kind, so the shape, the colour and the sentence a reader hears are
 * read off one answer: a caller writing its own words for the kind it hands the
 * icon is free to call a refusal a shortfall.
 */
export const ANALYTICS_INPUT_WARNING_TEXT: Record<
  AnalyticsInputWarningKind,
  string
> = {
  refusal: "Analytics tests cannot run.",
  shortfall: "Some samples would have no input.",
};

/** One sentence under the table, and what kind of thing it is claiming. */
export interface AnalyticsInputWarning {
  kind: AnalyticsInputWarningKind;
  message: string;
}

/**
 * What a flagged file's reach amounts to, or nothing worth saying.
 *
 * The one predicate behind both the sentence under the table and the marker on
 * the row, so the two cannot describe the same file differently.
 *
 * Matched to nothing is a refusal and not a shortfall, by the run's own
 * reckoning: it is `NO_MATCHED_EXPORT`, which the run stops on. A partial reach
 * is the one reading here that still generates. A table holding no samples
 * reaches this as a refusal too, which is the reading its sentence already
 * takes — nothing there is matched to anything.
 */
export function sampleReachKind(
  reaching: number,
  total: number,
): AnalyticsInputWarningKind | null {
  if (reaching === 0) return "refusal";
  if (reaching < total) return "shortfall";
  return null;
}

/** One document's reading in the sample column: the count, and its long form. */
export interface SampleReach {
  label: string;
  title: string;
  /**
   * What the count amounts to, for the marker beside it. Null on a file every
   * sample reaches, which is the reading that earns no marker at all.
   */
  kind: AnalyticsInputWarningKind | null;
}

/**
 * Whether the sample column has a question to ask.
 *
 * At least two samples, which is the condition the table-level sentence already
 * holds itself to: a one-sample table's file reaches all of it or none of it, so
 * `1 of 1` is a fraction that can only ever say one thing, and the consequence of
 * `0 of 1` is stated in prose one line away. A table holding no samples has
 * nothing to count against at all.
 *
 * Read both by the surfaces that draw the column and by the count inside it, so
 * a heading and its cells cannot come to different answers about whether the
 * column exists. The basis is not asked: the number of samples is the fact the
 * column depends on, and a basis check would be a second rule for it in different
 * words.
 */
export function hasSampleReachColumn(
  sourceRows: readonly TestTableSourceRow[],
): boolean {
  return sourceRows.length >= 2;
}

/**
 * How many of this test table's samples a document reaches, as the sample
 * column reads it.
 *
 * The row-level half of the shortfall the note under the table states as a
 * sentence: the sentence names one file and the matching left to do, and this
 * puts the same count on the row that holds the file, so nobody has to match a
 * filename by eye to find it.
 *
 * Drawn for every document rather than only the flagged one, because the count
 * is a fact about matching and holds whatever the flag says. A number that
 * appeared only on the flagged row would read as something the flag caused, and
 * an auditor deciding *which* file to flag is exactly who needs the other rows'
 * counts.
 *
 * Both numbers, and the total repeated on every row, for the reason the sentence
 * states both: the total is the same number down the column, which is what makes
 * a row falling short of it the row the eye finds.
 *
 * Silent wherever the column has no question to ask, so a table below the
 * threshold shows no count on either tab rather than one tab's idea of it. That
 * condition holds this to at least two samples, so "samples" needs no singular
 * form.
 *
 * The long form is carried because the label is two bare numbers: the Documents
 * tab draws its rows as a grid of divs, where nothing associates the column's
 * heading with the cell for a reader who cannot see the two lined up.
 *
 * It names the remedy only where the remedy is somewhere else. A count short of
 * the total reports a problem, and the control that fixes it is on the surface
 * that matches files to samples; a table scoped to one sample can state the
 * shortfall and cannot take the answer. Sent from the surface that already holds
 * the control, the same sentence would send an auditor where they are standing.
 */
export function sampleReach(
  sourceRows: readonly TestTableSourceRow[],
  documentId: BoundDocumentId,
  hasMatchEditor: boolean,
): SampleReach | null {
  if (!hasSampleReachColumn(sourceRows)) return null;
  const total = sourceRows.length;
  const reaching = samplesReaching(sourceRows, documentId);
  const matched = `Matched to ${reaching} of this test table's ${total} samples.`;
  return {
    label: `${reaching} of ${total}`,
    title: hasMatchEditor
      ? matched
      : `${matched} Change what it's matched to on the Documents tab.`,
    kind: sampleReachKind(reaching, total),
  };
}

/**
 * The sample row's reading in the same column.
 *
 * `all` rather than a count, because every sample has one of these rows by
 * definition: the rows above state a measurement that can come up short, and
 * this states an invariant that cannot. The total is still named, so the column
 * reads against one denominator down its whole length.
 *
 * Silent on the same condition as the counts above it, so the row cannot be the
 * one cell that keeps a withdrawn column on screen. The row itself stays: it is
 * the only subject a `sample_row` parameter can be bound from, and a table with
 * too few samples to count still has that parameter to fill.
 */
export function sampleRowReachLabel(
  sourceRows: readonly TestTableSourceRow[],
): string | null {
  if (!hasSampleReachColumn(sourceRows)) return null;
  return `all ${sourceRows.length}`;
}

/**
 * The required parameters no file will reach.
 *
 * This is `UNBOUND_REQUIRED_PARAMETER` and `NO_FLAGGED_EXPORT`, computed where
 * the auditor can still act on them. The run refuses the whole column over
 * either, and both are otherwise invisible until a Generate has already been
 * spent.
 *
 * A `sample_export` binding names no file — it names the rule *whichever
 * document this test table flags as its analytics input* — so it reads as
 * answered while resolving to nothing. With nothing flagged it is as unfed as a
 * parameter no row has answered at all, which is why one predicate covers both:
 * `boundSource !== null` is not the question.
 *
 * Scoped to required parameters because that is what the sentence below says,
 * and because widening it correctly cannot be done from here.
 *
 * **An optional parameter bound to the export refuses its column too, but not
 * every one of them does.** The run drops a bound input it calls `droppable` —
 * optional *and* carrying no stored value on the instance — and resolves the
 * column without it; an optional parameter the instance does hold a value for is
 * not droppable, so an unflagged table refuses the column exactly as a required
 * one would. That is the case with no wording, and it is narrower than
 * `!required`.
 *
 * Narrower than anything this can see, too: the projection behind these targets
 * declares whether a parameter is required and says nothing about stored values,
 * so the refusing and the droppable optional parameter are one shape client-side.
 * A predicate widened to `!required` would warn over every column that was going
 * to run, which is a worse reading than the silence it replaces. Saying the true
 * thing needs droppability carried on the wire.
 *
 * The flag reading is three-valued, and only a flag known absent licenses the
 * export half: `undefined` says the flag is unread, which is strictly less than
 * "nothing is flagged". Read as the latter it would announce a refusal off a
 * fact nobody stated, over a parameter the table may well be feeding. The
 * `boundSource === null` half asks the flag nothing and holds whatever it says.
 */
function unfedRequiredTargets(
  targets: readonly BindingTarget[],
  hasFlaggedExport: boolean | undefined,
): BindingTarget[] {
  return targets.filter(
    (target) =>
      target.required &&
      (target.boundSource === null ||
        (target.boundSource === "sample_export" && hasFlaggedExport === false)),
  );
}

/**
 * The table-level sentence naming them, or null when every required parameter is
 * filled.
 *
 * The parameter is qualified by its column only where the table has more than
 * one, which is the same rule the picker's headings follow: over a list one
 * column wholly owns, the column's name is the same word N times, and it crowds
 * out the names that tell the parameters apart.
 *
 * The consequence is stated with the state, because the state alone is not what
 * the auditor needs to know — an unfilled optional parameter looks identical and
 * costs nothing.
 *
 * The flag reading is the one above's, three values and all: an `undefined` here
 * names nothing bound to the export, and the sentence is built from whatever the
 * flag-independent half of that predicate still finds.
 */
function unfedRequiredWarning(
  targets: readonly BindingTarget[],
  hasFlaggedExport: boolean | undefined,
): string | null {
  const unfed = unfedRequiredTargets(targets, hasFlaggedExport);
  if (unfed.length === 0) return null;
  const qualified = new Set(targets.map((target) => target.columnId)).size > 1;
  const names = unfed.map((target) =>
    qualified ? `${target.columnName} · ${target.label}` : target.label,
  );
  if (names.length === 1) {
    return `${names[0]} is required and nothing fills it, so its analytics test cannot run.`;
  }
  const listed = `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${listed} are required and nothing fills them, so their analytics tests cannot run.`;
}

/**
 * The sentence for a flagged input that reaches some of the table's samples but
 * not all of them.
 *
 * Both numbers, not the shortfall alone, because they are different jobs: 4 of
 * 5 is one match away from done, 1 of 40 is a file matched to the wrong sample
 * and the reading is worth a second look before any matching starts.
 *
 * It ends on the action and names no verdict for the samples the file misses.
 * A sample no input reaches renders an amber "Could Not Generate" cell, and what
 * such a sample ought to conclude is an open product question, so a clause here
 * promising an outcome — insufficient evidence or any other — would be this
 * surface stating a result the product does not write. The shortfall is a fact
 * about the configuration; the verdict is not ours to announce.
 *
 * The condition holds it to at least two samples with at least one reached, so
 * "samples" needs no singular form: a one-sample table's file reaches all of it
 * or none of it, and both of those are stated elsewhere.
 */
function coverageShortfallWarning(
  name: string,
  reaching: number,
  total: number,
): string {
  const missing = total - reaching;
  const action =
    missing === 1
      ? "If it covers the other sample, match it to that one."
      : `If it covers the other ${missing}, match it to them.`;
  return `${name} reaches ${reaching} of these ${total} samples. ${action}`;
}

/**
 * Every table-level sentence this configuration earns, in reading order.
 *
 * These are the input refusals this surface can anticipate:
 * `UNBOUND_REQUIRED_PARAMETER` and `NO_FLAGGED_EXPORT`, which the unfed sentence
 * above states, then `SEVERAL_FLAGGED_EXPORTS` and `NO_MATCHED_EXPORT`, plus the
 * partial reach below — the one entry here that is not a refusal, because the
 * run it describes starts and answers some of the samples.
 *
 * A list rather than one sentence because the conditions are independent, and
 * the run reports only the first: it refuses on the first entry in key order, so
 * an auditor who fixed the one thing it named would meet the next on the next
 * Generate. Nothing here has to be spent to be read, so nothing is held back.
 *
 * Every export condition is conditioned on some parameter being bound to the
 * export, which is the run's own gate — it resolves the flag only for a column
 * that asks for it. A table with two flagged files and no such binding generates
 * fine, and a sentence there is a false alarm.
 *
 * The documents and the samples are the whole test table's, which is the grain
 * the flag is resolved at, and they come from the caller's own reads: this file
 * reaches no network, and a second read of the same session would be a second
 * answer to disagree with the rows on screen.
 *
 * A row whose flag the projection did not send is not a row known to be
 * unflagged, so with any flag unread the export refusals go unsaid rather than
 * said in the negative. The collision and the reach below count `true` alone and
 * need no such guard: an unread flag only ever keeps them quiet, and quiet is a
 * claim nobody has to disprove.
 */
export function inputRefusalWarnings(input: {
  targets: readonly BindingTarget[];
  documents: readonly TestTableDocument[];
  sourceRows: readonly TestTableSourceRow[];
  documentNames: ReadonlyMap<BoundDocumentId, string>;
}): AnalyticsInputWarning[] {
  const flagged = input.documents.filter(
    (document) => document.isAnalyticsInput === true,
  );
  const flagsRead = input.documents.every(
    (document) => document.isAnalyticsInput !== undefined,
  );
  const hasFlaggedExport = flagsRead ? flagged.length > 0 : undefined;
  const warnings: AnalyticsInputWarning[] = [];
  const unfed = unfedRequiredWarning(input.targets, hasFlaggedExport);
  if (unfed !== null) warnings.push({ kind: "refusal", message: unfed });
  const boundToExport = boundToSampleExport(input.targets);
  // With nothing bound to the export, none of the conditions below is the run's
  // to hit. With nothing flagged, either `unfed` has already stated the refusal
  // in the words a required parameter takes, or the flag is unread and there is
  // no flagged file to say anything about.
  if (!boundToExport || flagged.length === 0) return warnings;
  if (flagged.length > 1) {
    // Said apart from the sentence above rather than folded into it: an
    // unflagged table and a table flagging two files are one missing uniqueness
    // constraint, and two different jobs for the auditor — to go and flag a
    // file, or to unflag one of these. The files are not named, because which of
    // them to keep is the auditor's decision and a list of two invites reading
    // one of them as the answer.
    warnings.push({
      kind: "refusal",
      message: `${flagged.length} documents are flagged as this test table's analytics input. Exactly one must be flagged, so its analytics tests cannot run.`,
    });
    // A collision leaves no single file to name, and the run refuses over it
    // before it reads any match, so no match is read here either.
    return warnings;
  }
  const [only] = flagged;
  const documentId = documentKey(only);
  // Every sentence below is a count of the samples this file reaches, and a row
  // that names no document has no matches to count. Silence rather than a zero,
  // which would announce a refusal — matched to none of these samples — off a
  // reading nobody made.
  if (documentId === null) return warnings;
  // Counted over every sample of the test table, because nothing on this page
  // can know which samples a future Generate will select. That grain is exact
  // for the file matched to nothing — matched to none of the table's samples is
  // matched to none of any subset of them, so that sentence never fires on a run
  // that would have succeeded — and deliberately wider than a run for the
  // shortfall, which is why the shortfall sentence claims no refusal: a Generate
  // over a subset the file does cover answers every sample it selected, and the
  // shortfall still names real samples the file would leave with no input.
  const reaching = samplesReaching(input.sourceRows, documentId);
  const total = input.sourceRows.length;
  // Silent only on a file every sample can reach, which is the reading that
  // earns no kind at all.
  const kind = sampleReachKind(reaching, total);
  if (kind === null) return warnings;
  const name = input.documentNames.get(documentId);
  // The sentences exist to name the file to go and match, and a document id
  // names nothing to an auditor. A row this table cannot name at all is a row
  // with neither a stored name nor a readable file behind it.
  if (name === undefined) return warnings;
  if (kind === "refusal") {
    // A table holding no samples at all lands here too, and takes this wording
    // rather than a count: "0 of these 0 samples" reads as an arithmetic
    // accident, and the file is in fact matched to nothing.
    warnings.push({
      kind,
      message: `${name} is flagged as this test table's analytics input but is matched to none of these samples, so its analytics tests cannot run.`,
    });
    return warnings;
  }
  warnings.push({
    kind,
    message: coverageShortfallWarning(name, reaching, total),
  });
  return warnings;
}

/** One analytics column's targets, for a picker that groups by column. */
export interface TargetGroup {
  columnId: string;
  /**
   * What the group is headed with: the bound workflow's name, because that is
   * the thing the parameters below it belong to — they are projected from the
   * workflow's activated version, and the column declares none of them.
   *
   * Falls back to the analytics column's own name, which is the only other true
   * name the group has: a column carrying file parameters before it carries a
   * workflow name is a reachable state, and a heading that rendered empty would
   * leave its parameters reading as part of the group above.
   */
  heading: string;
  targets: BindingTarget[];
}

/**
 * Gather targets under the column that declared them.
 *
 * Grouped here rather than in the picker because the grouping is a property of
 * the data — `bindingTargets` flattens the columns `feedColumns` zipped, and
 * this puts them back — and because the picker's own question is only whether
 * to draw the headings.
 *
 * Keyed on the column and never on the heading: two columns can be bound to
 * workflows sharing a name, and merging them would offer one column's parameter
 * under the other's group.
 *
 * First-appearance order, which is the authoring order `feedColumns` preserved:
 * the auditor already reads these columns in that order on the test table, and
 * a picker that sorted them would be the one surface disagreeing.
 */
export function groupTargetsByColumn(
  targets: readonly BindingTarget[],
): TargetGroup[] {
  const groups: TargetGroup[] = [];
  const byColumn = new Map<string, TargetGroup>();
  for (const target of targets) {
    const existing = byColumn.get(target.columnId);
    if (existing !== undefined) {
      existing.targets.push(target);
      continue;
    }
    const group: TargetGroup = {
      columnId: target.columnId,
      heading: target.workflowName ?? target.columnName,
      targets: [target],
    };
    byColumn.set(target.columnId, group);
    groups.push(group);
  }
  return groups;
}

/** The pair, so the same `param_key` on two columns cannot collide. */
export function targetKey(target: {
  columnId: string;
  paramKey: string;
}): string {
  return `${target.columnId}::${target.paramKey}`;
}

export function documentAssignmentRequest(
  target: { columnId: string; paramKey: string },
  documentId: BoundDocumentId,
): DocumentAssignmentRequest {
  return {
    columnId: target.columnId,
    paramKey: target.paramKey,
    source: "document",
    documentId,
  };
}

export function sampleRowAssignmentRequest(target: {
  columnId: string;
  paramKey: string;
}): SampleRowAssignmentRequest {
  return {
    columnId: target.columnId,
    paramKey: target.paramKey,
    source: "sample_row",
  };
}

/**
 * The pairs this document answers, out of targets the caller already computed.
 *
 * Split from `boundTargetsForDocument` because the Documents tab's two analytics
 * cells share one `targets` array for the whole row, and each of them asks this
 * question of it.
 */
export function targetsAssignedTo(
  targets: readonly BindingTarget[],
  documentId: BoundDocumentId,
): BindingTarget[] {
  return targets.filter((target) => target.assignedDocumentId === documentId);
}

/** Every pair this document currently answers, across all analytics columns. */
export function boundTargetsForDocument(
  columns: readonly FeedColumn[],
  documentId: BoundDocumentId,
): BindingTarget[] {
  return targetsAssignedTo(bindingTargets(columns), documentId);
}
